import React, { useState, useEffect } from 'react'
import styled from 'styled-components'
import { Link, useLocation } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { FiMenu, FiX, FiPhone } from 'react-icons/fi'
import Logo from './Logo'

const NavbarContainer = styled.nav`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  background: rgba(15, 23, 42, 0.95);
  backdrop-filter: blur(20px);
  border-bottom: 1px solid rgba(59, 130, 246, 0.2);
  box-shadow: 0 1px 20px rgba(0, 0, 0, 0.3);
  z-index: 1000;
  transition: all 0.3s ease;
`

const NavbarContent = styled.div`
  max-width: var(--container-max-width);
  margin: 0 auto;
  padding: 0 var(--container-padding);
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 80px;
`

const NavLinks = styled.div`
  display: flex;
  align-items: center;
  gap: 2rem;

  @media (max-width: 768px) {
    display: none;
  }
`

const NavLink = styled(Link)<{ $isActive: boolean }>`
  font-weight: 600;
  color: ${props => props.$isActive ? 'var(--color-accent)' : 'var(--color-text)'};
  text-decoration: none;
  transition: all 0.3s ease;
  position: relative;
  padding: 0.5rem 1rem;
  border-radius: var(--border-radius);

  &:hover {
    color: var(--color-accent);
    background: rgba(59, 130, 246, 0.1);
    box-shadow: 0 4px 12px rgba(59, 130, 246, 0.2);
  }

  &::after {
    content: '';
    position: absolute;
    bottom: -4px;
    left: 50%;
    transform: translateX(-50%);
    width: ${props => props.$isActive ? '80%' : '0'};
    height: 2px;
    background: var(--gradient-accent);
    transition: width 0.3s ease;
    border-radius: 1px;
  }

  &:hover::after {
    width: 80%;
  }
`

const PhoneButton = styled.a`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: var(--gradient-accent);
  color: white;
  padding: 0.75rem 1.75rem;
  border-radius: 50px;
  text-decoration: none;
  font-weight: 600;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(37, 99, 235, 0.3);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(37, 99, 235, 0.4);
    color: white;
  }

  @media (max-width: 768px) {
    display: none;
  }
`

const MobileMenuButton = styled.button`
  display: none;
  background: none;
  border: none;
  font-size: 1.5rem;
  color: var(--color-text);
  cursor: pointer;
  padding: 0.5rem;
  z-index: 1002;
  position: relative;

  @media (max-width: 768px) {
    display: block;
  }
`

const MobileMenuOverlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 1001;
  display: none;

  @media (max-width: 768px) {
    display: block;
  }
`

const MobileMenu = styled(motion.div)`
  position: fixed;
  top: 80px;
  left: 0;
  right: 0;
  background: var(--color-bg-card);
  border-bottom: 1px solid var(--color-border);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.4);
  padding: 2rem var(--container-padding);
  z-index: 1001;
  max-height: calc(100vh - 80px);
  overflow-y: auto;
  display: none;

  @media (max-width: 768px) {
    display: block;
  }
`

const MobileNavLinks = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  margin-bottom: 2rem;
`

const MobileNavLink = styled(Link)<{ $isActive: boolean }>`
  font-weight: 600;
  font-size: 1.125rem;
  color: ${props => props.$isActive ? 'var(--color-primary)' : 'var(--color-text)'};
  text-decoration: none;
  padding: 0.75rem 0;
  border-bottom: 1px solid var(--color-border-light);
  transition: color 0.2s ease;

  &:hover {
    color: var(--color-primary);
  }

  &:last-child {
    border-bottom: none;
  }
`

const MobilePhoneButton = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  background: var(--gradient-accent);
  color: white;
  padding: 1rem;
  border-radius: var(--border-radius);
  text-decoration: none;
  font-weight: 600;
  font-size: 1.125rem;
  transition: all 0.2s ease;
  box-shadow: 0 4px 15px rgba(37, 99, 235, 0.3);

  &:hover {
    background: var(--color-secondary);
    color: white;
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(37, 99, 235, 0.4);
  }
`

const Navbar: React.FC = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const location = useLocation()

  const navItems = [
    { path: '/', label: 'Strona główna' },
    { path: '/uslugi', label: 'Usługi' },
    { path: '/o-nas', label: 'O nas' },
    { path: '/kontakt', label: 'Kontakt' }
  ]

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen)
  }

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false)
  }

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = 'unset'
    }

    // Cleanup on unmount
    return () => {
      document.body.style.overflow = 'unset'
    }
  }, [isMobileMenuOpen])

  // Close menu on route change
  useEffect(() => {
    setIsMobileMenuOpen(false)
  }, [location.pathname])

  return (
    <>
      <NavbarContainer>
        <NavbarContent>
          <Logo />
          
          <NavLinks>
            {navItems.map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                $isActive={location.pathname === item.path}
              >
                {item.label}
              </NavLink>
            ))}
          </NavLinks>

          <PhoneButton href="tel:510184702">
            <FiPhone />
            510 184 702
          </PhoneButton>

          <MobileMenuButton onClick={toggleMobileMenu}>
            {isMobileMenuOpen ? <FiX /> : <FiMenu />}
          </MobileMenuButton>
        </NavbarContent>
      </NavbarContainer>

      <AnimatePresence>
        {isMobileMenuOpen && (
          <>
            <MobileMenuOverlay
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              onClick={closeMobileMenu}
            />
            <MobileMenu
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.2 }}
            >
              <MobileNavLinks>
                {navItems.map((item) => (
                  <MobileNavLink
                    key={item.path}
                    to={item.path}
                    $isActive={location.pathname === item.path}
                    onClick={closeMobileMenu}
                  >
                    {item.label}
                  </MobileNavLink>
                ))}
              </MobileNavLinks>
              
              <MobilePhoneButton href="tel:510184702" onClick={closeMobileMenu}>
                <FiPhone />
                Zadzwoń: 510 184 702
              </MobilePhoneButton>
            </MobileMenu>
          </>
        )}
      </AnimatePresence>
    </>
  )
}

export default Navbar 